import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import api from "@/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { HighTrafficLoading, BrandedRetryLoading } from "@/components/LoadingSpinner";
import ElectorateSearch from "@/components/ElectorateSearch";
import { ParticipationConsent, TermsDialog } from "@/components/LegalContent";
import { personalisationVisible, socialShareUrl } from "@/lib/widgetConfig";
import { apiError } from "@/lib/apiError";

// Device detection helpers
const isIOS = () => /ipad|ipod|iphone/i.test(navigator.userAgent);
const isMobile = () => /ipad|ipod|iphone|android/i.test(navigator.userAgent);

// Robust copy to clipboard function that works on iOS/Safari and in iframes
async function copyToClipboard(text) {
  // Method 1: Modern Clipboard API (works on most browsers, needs HTTPS)
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.log('Clipboard API failed, trying fallback');
    }
  }
  
  // Method 2: iOS-specific workaround using selection ranges
  // This works better on iOS Safari where execCommand can be finicky
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    
    // Prevent zoom on iOS
    textArea.style.fontSize = "16px";
    
    // Position off-screen but not hidden (hidden elements can't be selected on iOS)
    textArea.style.position = "fixed";
    textArea.style.left = "0";
    textArea.style.top = "0";
    textArea.style.width = "2em";
    textArea.style.height = "2em";
    textArea.style.padding = "0";
    textArea.style.border = "none";
    textArea.style.outline = "none";
    textArea.style.boxShadow = "none";
    textArea.style.background = "transparent";
    textArea.style.opacity = "0";
    
    // Make it readonly to prevent keyboard popup on iOS
    textArea.setAttribute("readonly", "");
    
    document.body.appendChild(textArea);
    
    // iOS-specific: use setSelectionRange instead of select()
    if (isIOS()) {
      const range = document.createRange();
      range.selectNodeContents(textArea);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      textArea.setSelectionRange(0, 999999);
    } else {
      textArea.select();
    }
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      return true;
    }
  } catch (err) {
    console.log('execCommand fallback failed:', err);
  }
  
  return false;
}

// Open mailto link with iOS workaround
function openMailtoLink(mailtoUrl) {
  if (isIOS()) {
    // iOS Safari needs anchor-click workaround for reliable mailto opening
    const link = document.createElement('a');
    link.href = mailtoUrl;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    window.location.href = mailtoUrl;
  }
}

// iOS Safari has a strict ~2000 char limit on mailto URLs.
// Build a safe mailto for iOS by truncating body if needed.
const IOS_MAILTO_MAX = 1800;
function buildIOSSafeMailto(mailtoUrl) {
  if (!mailtoUrl || mailtoUrl.length <= IOS_MAILTO_MAX) return mailtoUrl;
  try {
    const parsed = parseMailto(mailtoUrl);
    const base = `mailto:${encodeURIComponent(parsed.to)}?subject=${encodeURIComponent(parsed.subject)}`;
    const bodyBudget = IOS_MAILTO_MAX - base.length - 6; // 6 = "&body="
    if (bodyBudget <= 0) return base;
    // Truncate body to fit, appending a note
    const note = "\n\n(Full email body available via copy/paste below)";
    const maxRaw = Math.floor(bodyBudget / 3) - note.length; // URL encoding roughly 3x
    if (maxRaw <= 0) return base;
    const trimmed = parsed.body.substring(0, maxRaw).trimEnd() + note;
    return `${base}&body=${encodeURIComponent(trimmed)}`;
  } catch {
    return mailtoUrl;
  }
}

// Parse mailto URL into components
function parseMailto(mailtoUrl) {
  try {
    // mailto:email@example.com?subject=X&body=Y&bcc=Z
    const [, rest] = mailtoUrl.split('mailto:');
    if (!rest) return { to: '', subject: '', body: '', bcc: '' };
    
    const [toWithParams] = rest.split('?');
    const to = decodeURIComponent(toWithParams || '');
    
    const urlParams = new URLSearchParams(rest.includes('?') ? rest.split('?')[1] : '');
    return {
      to,
      subject: urlParams.get('subject') || '',
      body: urlParams.get('body') || '',
      bcc: urlParams.get('bcc') || ''
    };
  } catch (e) {
    console.error('Failed to parse mailto:', e);
    return { to: '', subject: '', body: '', bcc: '' };
  }
}

// Build Gmail compose URL
function buildGmailUrl(to, subject, body, bcc) {
  const params = new URLSearchParams();
  params.set('view', 'cm');
  if (to) params.set('to', to);
  if (subject) params.set('su', subject);
  if (body) params.set('body', body);
  if (bcc) params.set('bcc', bcc);
  return `https://mail.google.com/mail/?${params.toString()}`;
}

// Build Outlook compose URL
function buildOutlookUrl(to, subject, body, bcc) {
  const params = new URLSearchParams();
  if (to) params.set('to', to);
  if (subject) params.set('subject', subject);
  if (body) params.set('body', body);
  if (bcc) params.set('bcc', bcc);
  return `https://outlook.live.com/mail/0/deeplink/compose?${params.toString()}`;
}

// Get/set email provider preference from localStorage
const EMAIL_PREF_KEY = 'civipen_email_provider';
function getEmailPreference() {
  try {
    return localStorage.getItem(EMAIL_PREF_KEY);
  } catch {
    return null;
  }
}
function setEmailPreference(provider) {
  try {
    localStorage.setItem(EMAIL_PREF_KEY, provider);
  } catch {
    // localStorage not available
  }
}

// API call with automatic retry on 503 (server busy)
async function apiCallWithRetry(apiCall, { maxRetries = 3, retryDelay = 2000, onRetryStart, onRetryEnd } = {}) {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await apiCall();
      if (onRetryEnd) onRetryEnd();
      return result;
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      
      // Permanent errors — DON'T retry, surface immediately.
      // 402 = AI quota exhausted (billing issue), 502 = invalid AI key.
      if (status === 402 || status === 502 || detail === 'ai_quota_exhausted' || detail === 'ai_key_invalid') {
        if (onRetryEnd) onRetryEnd();
        throw error;
      }
      
      // Only retry on 503 (server busy) or 429 (rate limited)
      if ((status === 503 || status === 429) && attempt < maxRetries) {
        console.log(`[API] Server busy (${status}), retry ${attempt}/${maxRetries} in ${retryDelay}ms`);
        if (onRetryStart) onRetryStart(attempt);
        
        // Wait before retry (use Retry-After header if provided)
        const retryAfter = error?.response?.headers?.['retry-after'];
        const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : retryDelay;
        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
      }
      
      // Non-retryable error or max retries reached
      if (onRetryEnd) onRetryEnd();
      throw error;
    }
  }
  
  throw lastError;
}

// Translate a generate API error into a friendly, accurate message.
function describeGenerateError(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (status === 402 || detail === 'ai_quota_exhausted') {
    return "AI email generation is unavailable because the AI service has hit its usage limit. Please contact the campaign organiser — they need to top up their AI account.";
  }
  if (status === 502 || detail === 'ai_key_invalid') {
    return "AI email generation is unavailable due to a configuration problem. Please contact the campaign organiser.";
  }
  if (status === 503) {
    return "We couldn't generate your email right now. Please try again in a few moments.";
  }
  return "Failed to generate email. Please try again.";
}

export default function Widget(){
  const { id } = useParams();
  const [camp, setCamp] = useState(null);
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [mailto, setMailto] = useState("");
  const [bcc, setBcc] = useState([]);
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTerms,setShowTerms]=useState(false);
  const [retryLoading, setRetryLoading] = useState(false);  // For 503 branded loading overlay
  const [retryCount, setRetryCount] = useState(0);  // Track retry attempts

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pers, setPers] = useState("");
  const [postcode, setPostcode] = useState("");
  const [matchedMPs, setMatchedMPs] = useState([]);
  const [postcodeVerified, setPostcodeVerified] = useState(false);
  const [electorateOptions, setElectorateOptions] = useState([]);
  const [showElectorateModal, setShowElectorateModal] = useState(false);
  const [selectedElectorateId, setSelectedElectorateId] = useState(null);
  const [step, setStep] = useState("compose");
  const [consentChecked, setConsentChecked] = useState(false);
  const [signatureCount, setSignatureCount] = useState(0);
  const [hasSigned, setHasSigned] = useState(false);
  const [phone, setPhone] = useState("");  // For Submission Builder
  const [address, setAddress] = useState("");  // For Submission Builder (when require_address is enabled)
  const [submissionType, setSubmissionType] = useState("public");  // public, name_withheld, private
  const [preamble, setPreamble] = useState("");  // Preamble from API response
  // Social Surge state
  const [generatedFacebookPost, setGeneratedFacebookPost] = useState("");
  const [generatedTweet, setGeneratedTweet] = useState("");
  const [socialStep, setSocialStep] = useState("compose");  // "compose" or "share"
  const [showEmailProviderModal, setShowEmailProviderModal] = useState(false);  // Desktop email provider choice
  const loc = useLocation();

// Ensure selected Google fonts are loaded on the fly
function ensureGoogleFontLoaded(fontName){
  if(!fontName) return;
  const fam = String(fontName).trim();
  const id = `gf-${fam.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id; link.rel = 'stylesheet';
  const familyParam = fam.replace(/\s+/g, '+');
  link.href = `https://fonts.googleapis.com/css2?family=${familyParam}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

// Build a properly-quoted font-family value with system fallbacks.
function fontStack(name){
  const n = (name || 'Inter').trim().replace(/"/g, '');
  return `"${n}", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
}

function ensureGoogleFontLoaded(fontName){
  if(!fontName) return;
  const fam = String(fontName).trim();
  const id = `gf-${fam.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  // Use + for spaces (Google Fonts expects this format)
  const familyParam = fam.replace(/\s+/g, '+');
  link.href = `https://fonts.googleapis.com/css2?family=${familyParam}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}
// font loader must be outside conditional returns to avoid hook-order issues
useEffect(()=>{
  ensureGoogleFontLoaded(camp?.theme?.font || 'Inter');
  ensureGoogleFontLoaded(camp?.theme?.font_title || camp?.theme?.font || 'Inter');
}, [camp?.theme?.font, camp?.theme?.font_title]);


  useEffect(()=>{
    const run = async ()=>{
      try{ 
        const r = await api.get(`/campaigns/${id}`); 
        setCamp(r.data);
        
        // Update page title to "Campaign Name | Civipen"
        if (r.data.title) {
          document.title = `${r.data.title} | Civipen`;
        }
        
        // View tracking is now done SERVER-SIDE when campaign is fetched (can't be blocked by ad blockers)
        console.log('[ANALYTICS] View tracked server-side for campaign:', id);
        
        // Fetch signature count for Sign & Send campaigns
        if (r.data.campaign_type === "sign_and_send") {
          try {
            const sigRes = await api.get(`/widget/${id}/signature-count`);
            setSignatureCount(sigRes.data.count || 0);
          } catch (e) {
            console.error("Failed to fetch signature count:", e);
          }
        }
      }catch(e){ toast("Campaign not found"); }
    }; run();
    
    // Cleanup: Reset title when leaving widget
    return () => {
      document.title = "Civipen | Overton Solutions";
    };
  },[id]);

  // Sign the petition (for Sign & Send campaigns)
  const doSign = async () => {
    const signConfig = camp?.sign_send_config || {};
    
    // Email is ALWAYS required for Sign & Send (can't sign without identification)
    if (!email.trim()) {
      toast.error("Please enter your email address to sign the petition");
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    // Postcode is REQUIRED if recipient_mode is "postcode" (need to find their MP)
    if (signConfig.recipient_mode === "postcode") {
      if (!postcode.trim()) {
        toast.error("Please enter your postcode to find your local MP");
        return;
      }
      if (!postcodeVerified || matchedMPs.length === 0) {
        toast.error("Please verify your postcode to find your representative");
        return;
      }
    }
    
    // T&Cs consent is ALWAYS required for Sign & Send (we're collecting data)
    if (!consentChecked) {
      toast.error("Please agree to the Terms & Conditions to sign the petition");
      return;
    }
    
    setLoading(true);
    try {
      // Submit signature with personalisation
      const signPayload = {
        user_name: name || undefined,
        user_email: email.trim(),
        consent: consentChecked,
        postcode: postcode.trim() || undefined,
        personalisation: personalisationVisible(camp?.show_personalisation, camp?.input_requirements?.personalisation) ? pers : undefined
      };
      await api.post(`/widget/${id}/sign`, signPayload);
      
      // Update signature count
      setSignatureCount(prev => prev + 1);
      setHasSigned(true);
      
      toast.success("Thank you for signing!");
      
      // Track signature event
      try { 
        await api.post('/analytics/event', { campaign_id: id, org_id: camp.org_id, type: 'signature', meta: {} }); 
      } catch {}
      
      // Now proceed to generate the email
      await doGenerateAfterSign();
      
    } catch (e) {
      console.error("Sign error:", e);
      toast.error(e?.response?.data?.detail || "Failed to sign petition");
    } finally {
      setLoading(false);
    }
  };
  
  // Generate email after signing (for Sign & Send)
  const doGenerateAfterSign = async () => {
    if (!camp) return;
    
    const signConfig = camp?.sign_send_config || {};
    const req = { 
      campaign_id: camp.id, 
      user_name: name || undefined, 
      user_email: email || undefined, 
      personalisation: personalisationVisible(camp?.show_personalisation, camp?.input_requirements?.personalisation) ? pers : undefined,
      consent: consentChecked 
    };
    
    // Add matched MP emails for postcode mode
    if (signConfig.recipient_mode === "postcode" && matchedMPs.length > 0) {
      req.matched_mp_emails = matchedMPs.map(mp => mp.email).filter(Boolean);
    }
    
    try {
      const r = await apiCallWithRetry(
        () => api.post(`/widget/${camp.id}/generate`, req),
        {
          maxRetries: 3,
          retryDelay: 2000,
          onRetryStart: (attempt) => {
            setRetryLoading(true);
            setRetryCount(attempt);
          },
          onRetryEnd: () => {
            setRetryLoading(false);
            setRetryCount(0);
          }
        }
      );
      setBody(r.data.body);
      setSubject(r.data.subject);
      setMailto(r.data.mailto);
      setWarning(r.data.warning || "");
      setBcc(r.data.bcc || []);
      setStep("send");
      // Generate tracking is now done SERVER-SIDE (can't be blocked by ad blockers)
      console.log('[ANALYTICS] Generate tracked server-side for campaign:', camp.id);
    } catch (e) {
      setRetryLoading(false);
      setRetryCount(0);
      toast.error(describeGenerateError(e));
    }
  };

  const verifyPostcode = async (electorateId = null, postcodeOverride = null) => {
    const postcodeToUse = postcodeOverride || postcode;
    if (!postcodeToUse || !postcodeToUse.trim()) {
      toast.error("Please enter your postcode or select an electorate");
      return;
    }
    
    // Update postcode state if override provided
    if (postcodeOverride) {
      setPostcode(postcodeOverride);
    }
    
    console.log("[VERIFY POSTCODE] Starting...", { postcode: postcodeToUse, id, electorateId });
    setLoading(true);
    try {
      const payload = { postcode: postcodeToUse.trim() };
      if (electorateId) {
        payload.selected_electorate_id = electorateId;
      }
      
      console.log("[VERIFY POSTCODE] Making API call to:", `/widget/${id}/match-postcode`, payload);
      const r = await api.post(`/widget/${id}/match-postcode`, payload);
      console.log("[VERIFY POSTCODE] Response received:", r);
      const data = r.data;
      
      if (data && data.ok) {
        // Check if multiple electorates need selection
        if (data.multiple_electorates && data.electorates && data.electorates.length > 0) {
          setElectorateOptions(data.electorates);
          setShowElectorateModal(true);
          setLoading(false);
          // Don't show error toast - this is expected behavior
          return;
        }
        
        // Single electorate or electorate selected - show MPs
        if (data.mps && data.mps.length > 0) {
          setMatchedMPs(data.mps);
          setPostcodeVerified(true);
          setShowElectorateModal(false);
          toast.success(`Found ${data.mps.length} MP(s)`);
        } else {
          toast.error("No MPs found");
          setMatchedMPs([]);
          setPostcodeVerified(false);
        }
      } else {
        toast.error("Failed to verify postcode");
        setMatchedMPs([]);
        setPostcodeVerified(false);
      }
    } catch (e) {
      console.error("[VERIFY POSTCODE] Error:", e);
      const errorMsg = e?.response?.data?.detail || "Invalid postcode or not found";
      toast.error(errorMsg);
      setMatchedMPs([]);
      setPostcodeVerified(false);
      setShowElectorateModal(false);
    } finally {
      console.log("[VERIFY POSTCODE] Finally block");
      setLoading(false);
    }
  };
  
  const selectElectorate = (electorateId) => {
    setSelectedElectorateId(electorateId);
    verifyPostcode(electorateId);
  };

  const doGenerate = async ()=>{
    if (!camp) return;
    
    // For MP Ping campaigns, verify postcode first
    if (camp.campaign_type === "mp_ping") {
      if (!postcodeVerified || matchedMPs.length === 0) {
        toast("Please enter and verify your postcode first");
        return;
      }
    }
    
    // For Submission Builder, validate required fields
    if (camp.campaign_type === "submission_builder") {
      if (!name.trim()) {
        toast.error("Please enter your name (required for submissions)");
        return;
      }
      if (!email.trim()) {
        toast.error("Please enter your email (required for submissions)");
        return;
      }
      if (!phone.trim()) {
        toast.error("Please enter your phone number (required for submissions)");
        return;
      }
      if (camp.submission_config?.require_address && !address.trim()) {
        toast.error("Please enter your address (required for this submission)");
        return;
      }
    }
    
    const req = { 
      campaign_id: camp.id, 
      user_name: name||undefined, 
      user_email: email||undefined, 
      user_phone: phone||undefined,  // For Submission Builder
      user_address: address||undefined,  // For Submission Builder when require_address is enabled
      personalisation: personalisationVisible(camp?.show_personalisation, camp?.input_requirements?.personalisation) ? pers : undefined,
      consent: consentChecked,
      submission_type: camp.campaign_type === "submission_builder" ? submissionType : undefined
    };
    
    // Add matched MP emails for MP Ping campaigns
    if (camp.campaign_type === "mp_ping") {
      req.matched_mp_emails = matchedMPs.map(mp => mp.email).filter(Boolean);
    }
    
    if (camp.campaign_type !== "submission_builder") {
      // Standard validation for other campaign types
      if (camp?.input_requirements?.require_email && !email){ toast("Please enter your email"); return; }
      if (camp?.input_requirements?.require_name && !name){ toast("Please enter your name"); return; }
    }
    if (isConsentRequired && !consentChecked){ toast("Please consent to the Terms & Conditions"); return; }
    setLoading(true);
    try{
      const r = await apiCallWithRetry(
        () => api.post(`/widget/${camp.id}/generate`, req),
        {
          maxRetries: 3,
          retryDelay: 2000,
          onRetryStart: (attempt) => {
            setRetryLoading(true);
            setRetryCount(attempt);
          },
          onRetryEnd: () => {
            setRetryLoading(false);
            setRetryCount(0);
          }
        }
      );
      setBody(r.data.body); setSubject(r.data.subject); setMailto(r.data.mailto); setWarning(r.data.warning||""); setBcc(r.data.bcc||[]);
      if (r.data.preamble) setPreamble(r.data.preamble);
      setStep("send");
      // Generate tracking is now done SERVER-SIDE (can't be blocked by ad blockers)
      console.log('[ANALYTICS] Generate tracked server-side for campaign:', camp.id);
    }catch(e){ 
      console.error('[GENERATE ERROR]', e);
      setRetryLoading(false);
      setRetryCount(0);
      toast.error(describeGenerateError(e));
    }
    finally{ setLoading(false); }
  };

  const isConsentRequired = useMemo(()=>{
    return !!camp;
  },[camp]);

  // Handle opening email - shows provider choice on desktop, uses mailto on mobile
  const handleOpenEmail = async (mailtoUrl, trackAnalytics = true) => {
    // Track analytics first
    if (trackAnalytics && camp) {
      try { 
        await api.post('/analytics/event', { campaign_id: camp.id, org_id: camp.org_id, type: 'click', meta: {} }); 
        console.log('[ANALYTICS] Click tracked for campaign:', camp.id);
      } catch(err) { 
        console.error('[ANALYTICS] Failed to track click:', err); 
      }
    }
    
    // Mobile: use mailto directly (with iOS workaround)
    if (isMobile()) {
      openMailtoLink(mailtoUrl);
      return;
    }
    
    // Desktop: check for saved preference
    const savedPref = getEmailPreference();
    if (savedPref) {
      openWithProvider(savedPref, mailtoUrl);
      return;
    }
    
    // Desktop with no preference: show modal
    setShowEmailProviderModal(true);
  };

  // Open email with specific provider
  const openWithProvider = (provider, mailtoUrl) => {
    const parsed = parseMailto(mailtoUrl || mailto);
    
    switch (provider) {
      case 'gmail':
        window.open(buildGmailUrl(parsed.to, parsed.subject, parsed.body, parsed.bcc), '_blank');
        break;
      case 'outlook':
        window.open(buildOutlookUrl(parsed.to, parsed.subject, parsed.body, parsed.bcc), '_blank');
        break;
      case 'desktop':
      default:
        openMailtoLink(mailtoUrl || mailto);
        break;
    }
  };

  // Handle provider selection from modal
  const handleProviderSelect = (provider, remember = false) => {
    if (remember) {
      setEmailPreference(provider);
    }
    setShowEmailProviderModal(false);
    openWithProvider(provider, mailto);
  };

  if (!camp) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="widget-loading">
      <HighTrafficLoading />
    </div>
  );

  const t = {
    ...(camp?.theme || {}),
    show_logo: (camp?.theme?.show_logo ?? true),
    show_title: (camp?.theme?.show_title ?? true),
    font: camp?.theme?.font || 'Inter',
    font_title: camp?.theme?.font_title || camp?.theme?.font || 'Inter',
    primary: camp?.theme?.primary || camp?.org_theme?.primary || '#0a2a66',
    primaryText: camp?.theme?.primaryText || '#ffffff',
    secondary: camp?.theme?.secondary || camp?.org_theme?.secondary || '#eef2ff',
    fieldText: camp?.theme?.fieldText || '#0a0a0a',
    fieldBorder: camp?.theme?.fieldBorder || camp?.org_theme?.fieldBorder || '#d1d5db',
    titleColor: camp?.theme?.titleColor || camp?.theme?.text || '#0a0a0a',
    bg: camp?.theme?.bg || '#ffffff',
    text: camp?.theme?.text || '#000000'
  };
  
  // Ensure font values are never empty strings
  const safeFont = t.font || 'Inter';
  const safeFontTitle = t.font_title || t.font || 'Inter';
  const fontFamilyBody = fontStack(safeFont);
  const fontFamilyTitle = fontStack(safeFontTitle);
  // moved to a stable hook before early returns to avoid hook-order mismatch

  // Set default placeholder for name based on campaign type
  const defaultNamePlaceholder = camp?.campaign_type === "submission_builder" ? 'Your full name' : 'Your name';
  
  const ph = {
    name: camp?.input_placeholders?.name || defaultNamePlaceholder,
    email: camp?.input_placeholders?.email || 'Your email',
    personalisation: (camp?.input_placeholders?.personalisation || 'Optional personalisation (one or two sentences)').substring(0, 80)
  };

  const params = new URLSearchParams(loc.search);
  const outerBg = params.get('outerBackground') || params.get('outerBG') || params.get('outer_bg') || t.bg;

  // Render subtitle with bold tags and newlines
  const renderSubtitle = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, lineIdx) => {
      const parts = line.split(/(\*\*[^*]+\*\*|<b>[^<]+<\/b>)/g);
      const rendered = parts.map((part, partIdx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={partIdx}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('<b>') && part.endsWith('</b>')) {
          return <strong key={partIdx}>{part.slice(3, -4)}</strong>;
        }
        return part;
      });
      return (
        <span key={lineIdx}>
          {rendered}
          {lineIdx < lines.length - 1 && <br />}
        </span>
      );
    });
  };

  const fieldStyle = { background: t.secondary, backgroundColor: t.secondary, color: t.fieldText, border: `1px solid ${t.fieldBorder}`, fontFamily: fontFamilyBody, boxShadow: 'none', outline: 'none', opacity: 1, filter: 'none' };
  const inputInnerStyle = { background: t.secondary, backgroundColor: t.secondary, color: t.fieldText, borderColor: t.fieldBorder, fontFamily: fontFamilyBody };

  // Get padding from URL params (default: 24px for reasonable spacing when embedded)
  const widgetPadding = params.get('padding') || '24px';
  
  // Get scrollbar and button colors from theme
  const scrollbarColor = camp?.theme?.scrollbarColor || t.primary;
  const buttonColor = camp?.theme?.buttonColor || t.secondary;
  
  // Button styling from campaign settings
  const bs = camp?.button_style || {};
  const btnRadius = bs.rounded === 'none' ? '0' : bs.rounded === 'sm' ? '4px' : bs.rounded === 'lg' ? '12px' : bs.rounded === 'full' ? '9999px' : '6px';
  const btnPadding = bs.size === 'sm' ? '6px 12px' : bs.size === 'lg' ? '14px 28px' : '10px 20px';
  const btnFontSize = bs.size === 'sm' ? '12px' : bs.size === 'lg' ? '16px' : '14px';
  const btnAlign = bs.alignment === 'left' ? 'flex-start' : bs.alignment === 'right' ? 'flex-end' : bs.alignment === 'full' ? 'stretch' : 'center';
  const btnWidth = bs.alignment === 'full' ? '100%' : 'auto';
  const primaryBtnStyle = { backgroundColor: t.primary, color: t.primaryText, opacity: 1, filter: 'none', fontFamily: fontFamilyBody, borderRadius: btnRadius, padding: btnPadding, fontSize: btnFontSize, width: btnWidth };
  
  // Check if personalisation field should be shown
  const showPersonalisation = personalisationVisible(camp?.show_personalisation, camp?.input_requirements?.personalisation);
  const socialPlatforms = camp?.social_surge_config?.platforms || ["facebook", "x"];
  const xShareHref = socialShareUrl(generatedTweet, camp?.social_surge_config);

  return (
    <div className="min-h-full flex items-start justify-center widget-scope" style={{ background: outerBg, color: t.text, fontFamily: fontFamilyBody, padding: widgetPadding }}>
      <TermsDialog open={showTerms} onOpenChange={setShowTerms} organisation={camp?.org_name} />
      {/* Branded 503 Retry Loading Overlay */}
      {retryLoading && (
        <BrandedRetryLoading 
          logoUrl={camp?.org_logo_url} 
          retryCount={retryCount} 
          maxRetries={3} 
        />
      )}
      
      <style>{`
        /* Page-level scrollbar styling (for iframe) */
        html::-webkit-scrollbar, body::-webkit-scrollbar { width: 10px; height: 10px; }
        html::-webkit-scrollbar-track, body::-webkit-scrollbar-track { background: ${outerBg}; }
        html::-webkit-scrollbar-thumb, body::-webkit-scrollbar-thumb { background: ${scrollbarColor}; border-radius: 5px; }
        html::-webkit-scrollbar-thumb:hover, body::-webkit-scrollbar-thumb:hover { opacity: 0.8; }
        /* Firefox scrollbar */
        html, body { scrollbar-width: thin; scrollbar-color: ${scrollbarColor} ${outerBg}; }
        
        .widget-scope input::placeholder, .widget-scope textarea::placeholder { color: ${t.fieldText}; opacity: 0.85; }
        .widget-scope input::-webkit-input-placeholder, .widget-scope textarea::-webkit-input-placeholder { color: ${t.fieldText}; opacity: 0.85; }
        .widget-scope input:-ms-input-placeholder, .widget-scope textarea:-ms-input-placeholder { color: ${t.fieldText}; opacity: 0.85; }
        .widget-scope { font-family: ${fontFamilyBody} !important; }
        .widget-scope .title-font { font-family: ${fontFamilyTitle} !important; }
        .widget-scope input, .widget-scope textarea { background-color: ${t.secondary} !important; color: ${t.fieldText} !important; border: 1px solid ${t.fieldBorder} !important; box-shadow: none !important; }
        .widget-scope input:focus, .widget-scope textarea:focus { outline: none !important; box-shadow: none !important; }
        /* Custom scrollbar styling for elements inside widget */
        .widget-scope ::-webkit-scrollbar { width: 8px; height: 8px; }
        .widget-scope ::-webkit-scrollbar-track { background: ${t.bg}; border-radius: 4px; }
        .widget-scope ::-webkit-scrollbar-thumb { background: ${scrollbarColor}; border-radius: 4px; }
        .widget-scope ::-webkit-scrollbar-thumb:hover { opacity: 0.8; }
        /* Copy button styling */
        .widget-scope .copy-btn { background-color: ${buttonColor} !important; color: ${t.fieldText} !important; border: 1px solid ${t.fieldBorder} !important; }
        .widget-scope .copy-btn:hover { opacity: 0.9; }
      `}</style>
      
      {/* Show "Campaign Ended" message for inactive campaigns */}
      {camp.active === false ? (
        <Card className="w-full max-w-2xl shadow-xl border" style={{ background: t.bg, color: t.text, fontFamily: fontFamilyBody }}>
          <CardContent className="py-16 px-8 text-center">
            {camp?.org_logo_url && (
              <img src={camp.org_logo_url} alt="logo" className="h-16 mx-auto mb-8" />
            )}
            <h2 className="text-2xl font-semibold mb-2" style={{ color: t.titleColor || t.text }}>
              This campaign has ended
            </h2>
            <p className="text-sm opacity-70" style={{ color: t.text }}>
              Thank you for your interest.
            </p>
          </CardContent>
        </Card>
      ) : (
      <Card className="w-full max-w-2xl shadow-xl border relative overflow-hidden" style={{ background: t.bg, color: t.text, fontFamily: fontFamilyBody }}>
        {t.bgImage && (
          <div
            aria-hidden
            className="absolute inset-0 z-0 pointer-events-none"
            style={{
              backgroundImage: `url(${t.bgImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: (Number(t.bgBlur) || 0) > 0 ? `blur(${Number(t.bgBlur)}px)` : 'none',
              opacity: (t.bgImageOpacity == null ? 1 : Number(t.bgImageOpacity)),
            }}
          />
        )}
        {/* Signature Counter (top position for Sign & Send) */}
        {camp.campaign_type === "sign_and_send" && camp.sign_send_config?.show_counter && 
         camp.sign_send_config?.counter_position === "top" && 
         signatureCount >= (camp.sign_send_config?.show_counter_after || 0) && (
          <div className="text-center py-4 px-4" style={{ backgroundColor: t.secondary }}>
            <div className="text-3xl font-bold" style={{ color: t.primary }}>{signatureCount.toLocaleString()}</div>
            {(camp.sign_send_config?.counter_display_mode || "count_only") === "progress" ? (
              <>
                <div className="text-sm opacity-75" style={{ color: t.text }}>
                  {(camp.sign_send_config?.counter_progress_label || 'of {goal} signatures')
                    .replace('{count}', signatureCount.toLocaleString())
                    .replace('{goal}', (camp.sign_send_config?.signature_goal || 1000).toLocaleString())}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2 max-w-xs mx-auto">
                  <div 
                    className="h-2 rounded-full transition-all duration-500" 
                    style={{ 
                      backgroundColor: t.primary, 
                      width: `${Math.min(100, (signatureCount / (camp.sign_send_config?.signature_goal || 1000)) * 100)}%` 
                    }}
                  ></div>
                </div>
              </>
            ) : (
              <div className="text-sm opacity-75" style={{ color: t.text }}>{camp.sign_send_config?.counter_count_label || 'people have signed'}</div>
            )}
          </div>
        )}
        
        <CardHeader className="title-font relative z-10" >
          {(() => {
            const TITLE_SIZE = { lg:'text-lg', xl:'text-xl', '2xl':'text-2xl', '3xl':'text-3xl', '4xl':'text-4xl' };
            const SUBTITLE_SIZE = { xs:'text-xs', sm:'text-sm', base:'text-base', lg:'text-lg' };
            const ALIGN_CLASS = { left:'text-left items-start', center:'text-center items-center', right:'text-right items-end' };
            const FLEX_ALIGN = { left:'justify-start', center:'justify-center', right:'justify-end' };
            const logoAlign = t.logoAlign || 'center';
            const logoSize = Math.max(16, Math.min(160, Number(t.logoSize) || 48));
            const titleSizeCls = TITLE_SIZE[t.titleSize || '2xl'] || 'text-2xl';
            const titleAlignCls = ALIGN_CLASS[t.titleAlign || 'center'] || ALIGN_CLASS.center;
            const subtitleSizeCls = SUBTITLE_SIZE[t.subtitleSize || 'sm'] || 'text-sm';
            const subtitleAlignCls = ALIGN_CLASS[t.subtitleAlign || t.titleAlign || 'center'] || ALIGN_CLASS.center;
            const titleStyle = {
              color: t.titleColor,
              ...(t.titleShadow ? { textShadow: '0 2px 8px rgba(0,0,0,0.25)' } : {}),
              ...(t.titleBg ? { background: t.titleBg, padding: '4px 12px', borderRadius: 8, display: 'inline-block' } : {}),
            };
            return (
              <div className={`flex flex-col gap-3 ${ALIGN_CLASS[logoAlign].split(' ')[1]}`}>
                {camp?.org_logo_url && t.show_logo !== false && (
                  <div className={`flex ${FLEX_ALIGN[logoAlign]} w-full`}>
                    <img
                      src={camp.org_logo_url}
                      alt="logo"
                      data-testid="widget-logo"
                      style={{
                        height: `${logoSize}px`,
                        width: 'auto',
                        filter: t.logoShadow ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.25))' : 'none',
                      }}
                    />
                  </div>
                )}
                {t.show_title !== false && (
                  <div className={`w-full flex flex-col ${ALIGN_CLASS[t.titleAlign || 'center'].split(' ')[1]}`}>
                    <CardTitle className={`${titleSizeCls} title-font ${ALIGN_CLASS[t.titleAlign || 'center'].split(' ')[0]} w-full`} data-testid="widget-title" style={{ color: t.titleColor }}>
                      <span style={titleStyle}>{camp.title}</span>
                    </CardTitle>
                    {camp.show_subtitle && camp.subtitle && (
                      <div className={`${subtitleSizeCls} ${subtitleAlignCls.split(' ')[0]} mt-2 opacity-80 w-full`} style={{ fontFamily: fontFamilyBody, color: t.subtitleColor || t.text }}>{renderSubtitle(camp.subtitle)}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </CardHeader>
        <CardContent className="space-y-5 relative z-10">
          {/* Social Surge Campaign - AI-generated social media posts */}
          {camp.campaign_type === "social_surge" && (
            <div className="space-y-6" data-testid="social-surge-content">
              {/* Compose Step - Get user info and generate content */}
              {socialStep === "compose" && (
                <div className="space-y-4">
                  {/* Name/Email fields on same line */}
                  {(camp?.input_requirements?.require_name || camp?.input_requirements?.require_email) && (
                    <div className="flex gap-2">
                      {camp?.input_requirements?.require_name && (
                        <Input 
                          data-testid="widget-name" 
                          placeholder={ph.name || "Your name"} 
                          value={name} 
                          onChange={(e)=>setName(e.target.value)} 
                          style={fieldStyle}
                          className="flex-1"
                        />
                      )}
                      {camp?.input_requirements?.require_email && (
                        <Input 
                          data-testid="widget-email" 
                          type="email"
                          placeholder={ph.email || "Your email"} 
                          value={email} 
                          onChange={(e)=>setEmail(e.target.value)} 
                          style={fieldStyle}
                          className="flex-1"
                        />
                      )}
                    </div>
                  )}
                  
                  {/* Optional personalisation */}
                  {showPersonalisation && (
                    <Textarea 
                      data-testid="widget-personalisation" 
                      placeholder={ph.personalisation} 
                      value={pers} 
                      onChange={(e)=>setPers(e.target.value)} 
                      style={fieldStyle} 
                    />
                  )}
                  
                  {/* T&Cs for Social Surge */}
                  {isConsentRequired && (
                    <ParticipationConsent prefix="social" checked={consentChecked} onChange={setConsentChecked} onTerms={() => setShowTerms(true)} organisation={camp.org_name} />
                  )}
                  
                  <div style={{ display: 'flex', justifyContent: btnAlign }}>
                    <Button 
                      data-testid="generate-social-btn" 
                      onClick={async () => {
                        // Validate consent
                        if (isConsentRequired && !consentChecked) {
                          toast.error("Please agree to the terms and conditions");
                          return;
                        }
                        // Validate required fields
                        if (camp?.input_requirements?.require_name && !name.trim()) {
                          toast.error("Please enter your name");
                          return;
                        }
                        if (camp?.input_requirements?.require_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
                          toast.error("Please enter a valid email address");
                          return;
                        }
                        
                        setLoading(true);
                        try {
                          const r = await api.post(`/widget/${camp.id}/generate-social`, {
                            campaign_id: camp.id,
                            consent: consentChecked,
                            personalisation: showPersonalisation ? pers || undefined : undefined,
                            name: name || undefined,
                            email: email || undefined
                          });
                          if (!(socialPlatforms.includes("facebook") && r.data.facebook_post?.trim()) && !(socialPlatforms.includes("x") && r.data.tweet?.trim())) {
                            toast.error("No posts were generated. Please try again.");
                            return;
                          }
                          setGeneratedFacebookPost(r.data.facebook_post || "");
                          setGeneratedTweet(r.data.tweet || "");
                          setSocialStep("share");
                          // Track generate event
                          console.log('[ANALYTICS] Social generate tracked for campaign:', camp.id);
                        } catch (e) {
                          toast.error(apiError(e, "Failed to generate social posts. Please try again."));
                        } finally {
                          setLoading(false);
                        }
                      }} 
                      disabled={loading || (isConsentRequired && !consentChecked)} 
                      style={primaryBtnStyle}
                    >
                      {loading ? 'Generating...' : camp.button_style?.label || 'Generate Posts'}
                    </Button>
                  </div>
                  <div className="text-xs opacity-70" data-testid="safety-note">We will generate a custom AI-written post</div>
                </div>
              )}
              
              {/* Share Step - Show generated content and share buttons */}
              {socialStep === "share" && (
                <div className="space-y-6">
                  {/* Facebook Section */}
                  {socialPlatforms.includes("facebook") && generatedFacebookPost && (
                    <div className="space-y-3 p-4 rounded-lg border" style={{ backgroundColor: t.secondary, borderColor: t.fieldBorder }}>
                      <div className="flex items-center gap-2">
                        <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        <span className="font-semibold" style={{ color: t.text }}>Share on Facebook</span>
                      </div>
                      
                      <div className="text-sm" style={{ color: t.text }}>
                        <span className="font-medium">Step 1:</span> Copy this text
                      </div>
                      <Button
                        data-testid="social-copy-facebook"
                        className="w-full"
                        onClick={async () => {
                          const success = await copyToClipboard(generatedFacebookPost);
                          if (success) {
                            toast.success("Text copied!");
                          } else {
                            toast.error("Copy failed. Please select the text manually.");
                          }
                        }}
                        style={{ backgroundColor: '#1877F2', color: '#fff' }}
                      >
                        Copy Text
                      </Button>
                      <Textarea 
                        data-testid="social-facebook-post"
                        value={generatedFacebookPost}
                        onChange={(e) => setGeneratedFacebookPost(e.target.value)}
                        rows={4}
                        style={{ backgroundColor: t.bg, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }}
                      />
                      
                      <div className="text-sm" style={{ color: t.text }}>
                        <span className="font-medium">Step 2:</span> Click below to open Facebook, then paste the text
                      </div>
                      <a
                        data-testid="social-open-facebook"
                        href="https://www.facebook.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2"
                        style={{ backgroundColor: '#1877F2', color: '#fff', textDecoration: 'none' }}
                        onClick={() => {
                          // Track analytics (non-blocking)
                          api.post('/analytics/event', { campaign_id: camp.id, org_id: camp.org_id, type: 'share_facebook', meta: {} }).catch(() => {});
                        }}
                      >
                        Open Facebook
                      </a>
                    </div>
                  )}
                  
                  {/* X (Twitter) Section */}
                  {socialPlatforms.includes("x") && generatedTweet && (
                    <div className="space-y-3 p-4 rounded-lg border" style={{ backgroundColor: t.secondary, borderColor: t.fieldBorder }}>
                      <div className="flex items-center gap-2">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        <span className="font-semibold" style={{ color: t.text }}>Share on X</span>
                      </div>
                      
                      {camp.social_surge_config?.x_post_type === "quote_tweet" ? (
                        <>
                          <div className="text-sm" style={{ color: t.text }}>
                            Review your quote tweet text, then click below to post:
                          </div>
                          <div className="relative">
                            <Textarea 
                              data-testid="social-x-post"
                              value={generatedTweet}
                              onChange={(e) => setGeneratedTweet(e.target.value)}
                              rows={3}
                              maxLength={280}
                              style={{ backgroundColor: t.bg, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }}
                            />
                            <div className="text-xs text-right mt-1" style={{ color: t.fieldText }}>
                              {generatedTweet.length}/280
                            </div>
                          </div>
                          {camp.social_surge_config?.x_quote_tweet_url && (
                            <div className="text-xs p-2 rounded" style={{ backgroundColor: t.bg, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }}>
                              <span className="font-medium">Quoting:</span> {camp.social_surge_config.x_quote_tweet_url}
                            </div>
                          )}
                          <a
                            data-testid="social-share-x"
                            href={xShareHref} target="_blank" rel="noopener noreferrer"
                            className="w-full"
                            style={{ backgroundColor: '#000', color: '#fff', display: 'flex', justifyContent: 'center', padding: '10px 16px', borderRadius: 6 }}
                            onClick={() => { api.post('/analytics/event', { campaign_id: camp.id, org_id: camp.org_id, type: 'share_x', meta: { post_type: 'quote_tweet' } }).catch(() => {}); }}
                          >
                            Quote Tweet on X
                          </a>
                        </>
                      ) : camp.social_surge_config?.x_post_type === "reply" ? (
                        <>
                          <div className="text-sm" style={{ color: t.text }}>
                            Review your reply, then click below to post:
                          </div>
                          <div className="relative">
                            <Textarea 
                              data-testid="social-x-post"
                              value={generatedTweet}
                              onChange={(e) => setGeneratedTweet(e.target.value)}
                              rows={3}
                              maxLength={280}
                              style={{ backgroundColor: t.bg, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }}
                            />
                            <div className="text-xs text-right mt-1" style={{ color: t.fieldText }}>
                              {generatedTweet.length}/280
                            </div>
                          </div>
                          {camp.social_surge_config?.x_quote_tweet_url && (
                            <div className="text-xs p-2 rounded" style={{ backgroundColor: t.bg, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }}>
                              <span className="font-medium">Replying to:</span> {camp.social_surge_config.x_quote_tweet_url}
                            </div>
                          )}
                          <a
                            data-testid="social-share-x"
                            href={xShareHref} target="_blank" rel="noopener noreferrer"
                            className="w-full"
                            style={{ backgroundColor: '#000', color: '#fff', display: 'flex', justifyContent: 'center', padding: '10px 16px', borderRadius: 6 }}
                            onClick={() => { api.post('/analytics/event', { campaign_id: camp.id, org_id: camp.org_id, type: 'share_x', meta: { post_type: 'reply' } }).catch(() => {}); }}
                          >
                            Reply on X
                          </a>
                        </>
                      ) : (
                        <>
                          <div className="text-sm" style={{ color: t.text }}>
                            Review your post, then click below to share on X:
                          </div>
                          <div className="relative">
                            <Textarea 
                              data-testid="social-x-post"
                              value={generatedTweet}
                              onChange={(e) => setGeneratedTweet(e.target.value)}
                              rows={3}
                              maxLength={280}
                              style={{ backgroundColor: t.bg, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }}
                            />
                            <div className="text-xs text-right mt-1" style={{ color: t.fieldText }}>
                              {generatedTweet.length}/280
                            </div>
                          </div>
                          <a
                            data-testid="social-share-x"
                            href={xShareHref} target="_blank" rel="noopener noreferrer"
                            className="w-full"
                            style={{ backgroundColor: '#000', color: '#fff', display: 'flex', justifyContent: 'center', padding: '10px 16px', borderRadius: 6 }}
                            onClick={() => { api.post('/analytics/event', { campaign_id: camp.id, org_id: camp.org_id, type: 'share_x', meta: { post_type: 'basic' } }).catch(() => {}); }}
                          >
                            Post on X
                          </a>
                        </>
                      )}
                    </div>
                  )}
                  <Button data-testid="social-regenerate-button" type="button" variant="outline" onClick={() => setSocialStep("compose")} style={{ color: t.text, borderColor: t.fieldBorder }}>Generate new posts</Button>
                </div>
              )}
            </div>
          )}
          
          {step === "compose" && camp.campaign_type !== "social_surge" && (
            <div className="space-y-4" data-testid="compose-step">
              {/* Postcode/Electorate search for MP Ping campaigns */}
              {camp.campaign_type === "mp_ping" && (
                <div className="space-y-3">
                  <ElectorateSearch
                    campaignId={id}
                    theme={t}
                    loading={loading}
                    verified={postcodeVerified}
                    placeholder="Enter postcode or electorate"
                    targetLevel={camp.mp_ping_config?.target_level || "federal"}
                    targetState={camp.mp_ping_config?.target_state || ""}
                    onSelect={(selection) => {
                      if (selection) {
                        setPostcode(selection.postcode || '');
                      } else {
                        setPostcodeVerified(false);
                        setMatchedMPs([]);
                      }
                    }}
                    onVerify={(selection) => {
                      if (selection?.type === 'electorate' && selection?.value) {
                        // User selected a specific electorate - pass it directly to skip the modal
                        setPostcode(selection.postcode || '');
                        verifyPostcode(selection.value, selection.postcode);
                      } else if (selection?.postcode) {
                        // Just a postcode - may need to show modal for multiple electorates
                        setPostcode(selection.postcode);
                        verifyPostcode(null, selection.postcode);
                      }
                    }}
                  />
                  
                  {postcodeVerified && matchedMPs.length > 0 && (
                    <div className="text-sm p-3 rounded" style={{ backgroundColor: t.secondary, color: t.text }}>
                      <div className="font-semibold mb-2">Your Representatives ({matchedMPs.length}):</div>
                      <ul className={`space-y-1 ${matchedMPs.length > 3 ? 'max-h-24 overflow-y-auto' : ''}`}>
                        {matchedMPs.map((mp, idx) => (
                          <li key={idx} className="text-xs">
                            • {mp.name} ({mp.level === "federal" ? "Federal" : "State"} - {mp.electorate})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {/* Sign & Send: Postcode/Electorate search if postcode mode is active */}
              {camp.campaign_type === "sign_and_send" && camp.sign_send_config?.recipient_mode === "postcode" && (
                <div className="space-y-3">
                  <ElectorateSearch
                    campaignId={id}
                    theme={t}
                    loading={loading}
                    verified={postcodeVerified}
                    placeholder="Enter postcode or electorate *"
                    targetLevel={camp.sign_send_config?.mp_level || "federal"}
                    targetState={camp.sign_send_config?.target_state || ""}
                    onSelect={(selection) => {
                      if (selection) {
                        setPostcode(selection.postcode || '');
                      } else {
                        setPostcodeVerified(false);
                        setMatchedMPs([]);
                      }
                    }}
                    onVerify={(selection) => {
                      if (selection?.type === 'electorate' && selection?.value) {
                        // User selected a specific electorate - pass it directly to skip the modal
                        setPostcode(selection.postcode || '');
                        verifyPostcode(selection.value, selection.postcode);
                      } else if (selection?.postcode) {
                        // Just a postcode - may need to show modal for multiple electorates
                        setPostcode(selection.postcode);
                        verifyPostcode(null, selection.postcode);
                      }
                    }}
                  />
                  
                  {postcodeVerified && matchedMPs.length > 0 && (
                    <div className="text-sm p-3 rounded" style={{ backgroundColor: t.secondary, color: t.text }}>
                      <div className="font-semibold mb-2">Your Representatives ({matchedMPs.length}):</div>
                      <ul className={`space-y-1 ${matchedMPs.length > 3 ? 'max-h-24 overflow-y-auto' : ''}`}>
                        {matchedMPs.map((mp, idx) => (
                          <li key={idx} className="text-xs">
                            • {mp.name} ({mp.level === "federal" ? "Federal" : "State"} - {mp.electorate})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {/* Name and Email inputs - always on the same row */}
              {camp.campaign_type !== "sign_and_send" && camp.campaign_type !== "submission_builder" && (camp?.input_requirements?.require_name || camp?.input_requirements?.require_email) && (
                <div className="flex gap-2">
                  {camp?.input_requirements?.require_name && (
                    <Input data-testid="widget-name" placeholder={ph.name} value={name} onChange={(e)=>setName(e.target.value)} style={fieldStyle} className="flex-1" />
                  )}
                  {camp?.input_requirements?.require_email && (
                    <Input data-testid="widget-email" placeholder={ph.email} value={email} onChange={(e)=>setEmail(e.target.value)} style={fieldStyle} className="flex-1" />
                  )}
                </div>
              )}
              
              {/* Sign & Send: Name and Email on same row for compact layout */}
              {camp.campaign_type === "sign_and_send" && (
                <div className="flex gap-2">
                  <Input 
                    data-testid="widget-name" 
                    placeholder={ph.name} 
                    value={name} 
                    onChange={(e)=>setName(e.target.value)} 
                    style={fieldStyle}
                    className="flex-1"
                  />
                  <Input 
                    data-testid="widget-email" 
                    placeholder={`${ph.email} *`}
                    value={email} 
                    onChange={(e)=>setEmail(e.target.value)} 
                    style={fieldStyle}
                    className="flex-1"
                  />
                </div>
              )}
              
              {/* Submission Builder: Name, Email, Phone (all required) + Submission Type */}
              {camp.campaign_type === "submission_builder" && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input 
                      data-testid="widget-name" 
                      placeholder={`${ph.name} *`}
                      value={name} 
                      onChange={(e)=>setName(e.target.value)} 
                      style={fieldStyle}
                      className="flex-1"
                    />
                    <Input 
                      data-testid="widget-phone" 
                      placeholder={`${ph.phone || 'Your phone number'} *`}
                      value={phone} 
                      onChange={(e)=>setPhone(e.target.value)} 
                      style={fieldStyle}
                      className="flex-1"
                    />
                  </div>
                  <Input 
                    data-testid="widget-email" 
                    placeholder={`${ph.email} *`}
                    value={email} 
                    onChange={(e)=>setEmail(e.target.value)} 
                    style={fieldStyle}
                  />
                  
                  {/* Address field - only shown when require_address is enabled */}
                  {camp.submission_config?.require_address && (
                    <Input 
                      data-testid="widget-address" 
                      placeholder="Your full address *"
                      value={address} 
                      onChange={(e)=>setAddress(e.target.value)} 
                      style={fieldStyle}
                    />
                  )}
                  
                  {/* Submission Type Selector */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1" style={{ color: t.text }}>
                      Submission Type
                      <span className="cursor-help text-gray-400 hover:text-gray-600" title="Choose how your submission will be handled by the committee">ⓘ</span>
                    </label>
                    <div className="space-y-1">
                      <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-gray-50" style={{ color: t.text }}>
                        <input 
                          type="radio" 
                          name="submission_type" 
                          value="public" 
                          checked={submissionType === "public"} 
                          onChange={(e)=>setSubmissionType(e.target.value)}
                          className="mt-0.5"
                        />
                        <div>
                          <div className="font-medium">{camp.submission_config?.type_public_label || 'Public'}</div>
                          <div className="text-xs opacity-75 whitespace-pre-line">{camp.submission_config?.type_public_desc || 'Your name and submission will be published on the parliament website'}</div>
                        </div>
                      </label>
                      <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-gray-50" style={{ color: t.text }}>
                        <input 
                          type="radio" 
                          name="submission_type" 
                          value="name_withheld" 
                          checked={submissionType === "name_withheld"} 
                          onChange={(e)=>setSubmissionType(e.target.value)}
                          className="mt-0.5"
                        />
                        <div>
                          <div className="font-medium">{camp.submission_config?.type_withheld_label || 'Name Withheld'}</div>
                          <div className="text-xs opacity-75 whitespace-pre-line">{camp.submission_config?.type_withheld_desc || 'Your submission will be published, but your name will not appear'}</div>
                        </div>
                      </label>
                      <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-gray-50" style={{ color: t.text }}>
                        <input 
                          type="radio" 
                          name="submission_type" 
                          value="private" 
                          checked={submissionType === "private"} 
                          onChange={(e)=>setSubmissionType(e.target.value)}
                          className="mt-0.5"
                        />
                        <div>
                          <div className="font-medium">{camp.submission_config?.type_private_label || 'Private'}</div>
                          <div className="text-xs opacity-75 whitespace-pre-line">{camp.submission_config?.type_private_desc || 'Only the committee will see your submission - it will not be published'}</div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
              
              {showPersonalisation && (
                <Textarea data-testid="widget-personalisation" placeholder={ph.personalisation} value={pers} onChange={(e)=>setPers(e.target.value)} style={fieldStyle} />
              )}

              {isConsentRequired && (
                <ParticipationConsent checked={consentChecked} onChange={setConsentChecked} onTerms={() => setShowTerms(true)} organisation={camp.org_name} />
              )}

              
              {showElectorateModal && electorateOptions.length > 0 && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={()=>setShowElectorateModal(false)}>
                  <div className="bg-white max-w-lg w-full rounded shadow p-4 space-y-3" onClick={(e)=>e.stopPropagation()} style={{ fontFamily: fontFamilyBody }}>
                    <div className="font-semibold text-lg">Select Your Electorate</div>
                    <div className="text-sm text-gray-600">
                      Multiple electorates found for postcode {postcode}. Please select yours:
                    </div>
                    <div className="space-y-2 max-h-80 overflow-auto">
                      {electorateOptions.map((electorate) => (
                        <button
                          key={electorate.id}
                          onClick={() => selectElectorate(electorate.id)}
                          className="w-full text-left p-3 border rounded hover:bg-gray-50 transition"
                          style={{ fontFamily: fontFamilyBody }}
                        >
                          <div className="font-medium">{electorate.name}</div>
                          <div className="text-xs text-gray-500">
                            {electorate.state} - {electorate.level === "federal" ? "Federal" : "State"}
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={()=>setShowElectorateModal(false)}>Cancel</Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2" style={{ justifyContent: btnAlign }}>
                {camp.campaign_type === "sign_and_send" ? (
                  <Button 
                    data-testid="sign-btn" 
                    onClick={doSign} 
                    disabled={loading || (isConsentRequired && !consentChecked)} 
                    style={primaryBtnStyle}
                  >
                    {loading ? (hasSigned ? 'Writing your email...' : 'Signing...') : 'Sign & Send'}
                  </Button>
                ) : (
                  <Button 
                    data-testid="generate-btn" 
                    onClick={doGenerate} 
                    disabled={loading || (isConsentRequired && !consentChecked)} 
                    style={primaryBtnStyle}
                  >
                    {loading ? 'Generating...' : camp.campaign_type === "submission_builder" ? 'Generate Submission' : 'Generate'}
                  </Button>
                )}
              </div>
              {camp.campaign_type === "sign_and_send" && (
                <div className="text-xs text-center opacity-75 whitespace-pre-line" style={{ color: t.text }}>
                  {camp.sign_send_config?.button_caption || "By signing, you'll add your name to the petition and send an AI-written email to your representative"}
                </div>
              )}
              {camp.campaign_type === "submission_builder" && (
                <div className="text-xs whitespace-pre-line" style={{ color: t.text }}>
                  {camp.submission_config?.disclosure_text || `Your submission will include your name, email${camp.submission_config?.require_address ? ", phone number and address" : " and phone number"}. NOTE: Incomplete information will likely lead to submissions being rejected.`}
                </div>
              )}
              <div className="text-xs opacity-70" data-testid="safety-note">AI can make mistakes or express views not aligned with the organisation.</div>
            </div>
          )}

          {step === "send" && (
            <div className="space-y-5" data-testid="send-step">
              {/* Two-step process when BCC is not in mailto (many recipients) */}
              {warning && mailto && bcc.length > 15 ? (
                <div className="space-y-4">
                  {/* Step 1: Copy emails */}
                  <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-blue-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold">1</span>
                      <span className="font-semibold text-blue-900" style={{ fontFamily: fontFamilyBody }}>Copy the recipient emails</span>
                    </div>
                    <p className="text-sm text-blue-800 mb-3" style={{ fontFamily: fontFamilyBody }}>
                      Click below to copy all {bcc.length} {camp.campaign_type === "mp_ping" ? "MPs'" : "recipients'"} email addresses
                    </p>
                    <Button 
                      size="lg"
                      className="w-full font-semibold"
                      style={{ backgroundColor: '#2563eb', color: '#fff' }}
                      data-testid="step1-copy-bcc"
                      onClick={async () => {
                        const success = await copyToClipboard(bcc.join("; "));
                        if (success) {
                          toast.success("Emails copied! Now proceed to Step 2");
                        } else {
                          toast.error("Copy failed. Please select the text manually.");
                        }
                      }}
                    >
                      📋 Copy All {bcc.length} Email Addresses
                    </Button>
                  </div>
                  
                  {/* Step 2: Open email */}
                  <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-green-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold">2</span>
                      <span className="font-semibold text-green-900" style={{ fontFamily: fontFamilyBody }}>Open your email & paste</span>
                    </div>
                    <p className="text-sm text-green-800 mb-3" style={{ fontFamily: fontFamilyBody }}>
                      Click below to open your email app, then paste the copied emails into the <strong>BCC</strong> field
                    </p>
                    {isIOS() ? (
                      <a
                        href={buildIOSSafeMailto(mailto)}
                        target="_top"
                        data-testid="step2-send-email"
                        className="w-full font-semibold inline-flex items-center justify-center rounded-md py-3"
                        style={{ 
                          backgroundColor: '#16a34a', 
                          color: '#fff',
                          textDecoration: 'none',
                          display: 'block',
                          textAlign: 'center'
                        }}
                        onClick={() => {
                          api.post('/analytics/event', { campaign_id: camp.id, org_id: camp.org_id, type: 'click', meta: { source: 'ios_sign_send' } }).catch(() => {});
                        }}
                      >
                        ✉️ Open Email App & Send
                      </a>
                    ) : (
                      <Button 
                        size="lg"
                        className="w-full font-semibold"
                        style={{ backgroundColor: '#16a34a', color: '#fff' }}
                        data-testid="step2-send-email"
                        onClick={() => handleOpenEmail(mailto)}
                      >
                        ✉️ Open Email App & Send
                      </Button>
                    )}
                  </div>
                </div>
              ) : mailto ? (
                // iOS-specific: Use actual <a> tag for reliable mailto opening
                // Other platforms: Use button with JS handler for better UX
                isIOS() ? (
                  <a
                    href={buildIOSSafeMailto(mailto)}
                    target="_top"
                    data-testid="send-now-btn"
                    className="w-full font-semibold text-lg py-3 inline-flex items-center justify-center rounded-md"
                    style={{ 
                      backgroundColor: t.primary, 
                      color: "#fff",
                      textDecoration: 'none',
                      display: 'block',
                      textAlign: 'center'
                    }}
                    onClick={() => {
                      // Track analytics on click (fire-and-forget)
                      api.post('/analytics/event', { campaign_id: camp.id, org_id: camp.org_id, type: 'click', meta: { source: 'ios_send' } }).catch(() => {});
                    }}
                  >
                    {camp.campaign_type === "submission_builder" ? "Click Here to Send Submission" : "Click Here to Send Now"}
                  </a>
                ) : (
                  <Button 
                    data-testid="send-now-btn" 
                    className="w-full font-semibold text-lg py-3"
                    style={{ backgroundColor: t.primary, color: "#fff" }}
                    onClick={() => handleOpenEmail(mailto)}
                    disabled={loading}
                  >
                    {camp.campaign_type === "submission_builder" ? "Click Here to Send Submission" : "Click Here to Send Now"}
                  </Button>
                )
              ) : (
                <div className="text-amber-600 text-sm" data-testid="mailto-warning">{warning || 'The email link is too long for some clients. Please use copy/paste below.'}</div>
              )}

              <div className="space-y-2">
                <div className="text-sm font-medium" style={{ fontFamily: fontFamilyBody }}>
                  {camp.campaign_type === "submission_builder" 
                    ? "Button not working? Copy/paste this into a new email to the committee:" 
                    : "Button not working? Copy/paste this into a new email:"}
                </div>

                <div className="space-y-1 border rounded p-3" style={{ backgroundColor: t.secondary, color: t.fieldText, border: `1px solid ${t.fieldBorder}`, fontFamily: fontFamilyBody }}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">
                      {camp.campaign_type === "submission_builder" ? "Committee Email(s)" : `BCC (${bcc.length} recipients)`}
                    </div>
                    <Button size="sm" variant="secondary" className="copy-btn" data-testid="copy-bcc-button" onClick={async ()=>{ const success = await copyToClipboard(bcc.join("; ")); if (success) { toast.success("Copied!"); } else { toast.error("Copy failed"); } }}>Copy</Button>
                  </div>
                  <Textarea data-testid="bcc-text" readOnly value={bcc.join("; ")} style={{ fontFamily: fontFamilyBody, backgroundColor: t.secondary, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }} />
                </div>

                <div className="space-y-1 border rounded p-3" style={{ backgroundColor: t.secondary, color: t.fieldText, border: `1px solid ${t.fieldBorder}`, fontFamily: fontFamilyBody }}>
                  <div className="flex items-center justify-between"><div className="text-sm font-medium">Subject</div><Button size="sm" variant="secondary" className="copy-btn" data-testid="copy-subject-button" onClick={async ()=>{ const success = await copyToClipboard(subject); if (success) { toast.success("Copied!"); } else { toast.error("Copy failed"); } }}>Copy</Button></div>
                  <Input data-testid="subject-text" readOnly value={subject} style={{ fontFamily: fontFamilyBody, backgroundColor: t.secondary, color: t.fieldText, borderColor: t.fieldBorder }} className="bg-transparent" />
                </div>

                <div className="space-y-1 border rounded p-3" style={{ backgroundColor: t.secondary, color: t.fieldText, border: `1px solid ${t.fieldBorder}`, fontFamily: fontFamilyBody }}>
                  <div className="flex items-center justify-between"><div className="text-sm font-medium">Body</div><Button size="sm" variant="secondary" className="copy-btn" data-testid="copy-body-button" onClick={async ()=>{ const success = await copyToClipboard(body); if (success) { toast.success("Copied!"); } else { toast.error("Copy failed"); } }}>Copy</Button></div>
                  <Textarea data-testid="email-body" value={body} readOnly rows={12} style={{ fontFamily: fontFamilyBody, backgroundColor: t.secondary, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }} />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Button variant="ghost" data-testid="back-to-compose" onClick={()=>setStep('compose')}>Back</Button>
              </div>
            </div>
          )}
          
          {/* Email Provider Choice Modal (Desktop only) - rendered outside steps so it's always accessible */}
          {showEmailProviderModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={()=>setShowEmailProviderModal(false)}>
              <div className="bg-white max-w-md w-full rounded-lg shadow-xl p-6 space-y-4 mx-4" onClick={(e)=>e.stopPropagation()} style={{ fontFamily: fontFamilyBody }}>
                <div className="text-center">
                  <div className="text-xl font-semibold mb-1">Open Email In...</div>
                  <div className="text-sm text-gray-500">Choose how you'd like to send your email</div>
                </div>
                
                <div className="space-y-3">
                  {/* Gmail Option */}
                  <button
                    onClick={() => handleProviderSelect('gmail', true)}
                    className="w-full flex items-center gap-4 p-4 border-2 rounded-lg hover:border-red-400 hover:bg-red-50 transition group"
                    data-testid="email-provider-gmail"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-100 group-hover:bg-red-200">
                      <svg viewBox="0 0 24 24" className="w-6 h-6 text-red-600">
                        <path fill="currentColor" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                      </svg>
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-medium">Gmail</div>
                      <div className="text-xs text-gray-500">Opens in a new tab</div>
                    </div>
                  </button>
                  
                  {/* Outlook Option */}
                  <button
                    onClick={() => handleProviderSelect('outlook', true)}
                    className="w-full flex items-center gap-4 p-4 border-2 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition group"
                    data-testid="email-provider-outlook"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-100 group-hover:bg-blue-200">
                      <svg viewBox="0 0 24 24" className="w-6 h-6 text-blue-600">
                        <path fill="currentColor" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.58.23h-8.547v-6.959l1.6 1.18c.102.086.226.128.37.128.14 0 .263-.042.37-.128l6.93-5.07c.076-.052.14-.077.19-.077.09 0 .135.058.135.172v-.53zm0-1.654v.672l-7.48 5.5-1.2-.883V7.387h8.08c.23 0 .424.077.58.23.16.154.24.347.24.578l-.22-.462zM14.635 6.088V18.67H.578c-.23 0-.424-.076-.58-.23-.157-.154-.235-.346-.235-.577V6.626c0-.23.078-.424.235-.578.156-.154.35-.23.58-.23h13.418c.077 0 .158.03.242.09.084.058.15.12.2.18H14.635zm-7.15 9.23c.887 0 1.593-.286 2.116-.858.524-.573.786-1.323.786-2.25 0-.942-.26-1.702-.78-2.28-.52-.58-1.22-.87-2.1-.87-.89 0-1.6.29-2.13.866-.53.577-.795 1.338-.795 2.284 0 .93.262 1.677.784 2.243.522.567 1.227.85 2.117.85h0zm.023-1.398c-.392 0-.698-.153-.918-.46-.22-.307-.33-.73-.33-1.27 0-.556.11-.99.33-1.302.22-.313.527-.47.918-.47.39 0 .695.155.914.465.22.31.33.747.33 1.31 0 .554-.11.98-.33 1.277-.22.3-.524.45-.914.45z"/>
                      </svg>
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-medium">Outlook / Hotmail</div>
                      <div className="text-xs text-gray-500">Opens in a new tab</div>
                    </div>
                  </button>
                  
                  {/* Desktop Email App Option */}
                  <button
                    onClick={() => handleProviderSelect('desktop', true)}
                    className="w-full flex items-center gap-4 p-4 border-2 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition group"
                    data-testid="email-provider-desktop"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200">
                      <svg viewBox="0 0 24 24" className="w-6 h-6 text-gray-600">
                        <path fill="currentColor" d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                      </svg>
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-medium">Use my desktop email app</div>
                      <div className="text-xs text-gray-500">Apple Mail, Outlook app, Thunderbird, etc.</div>
                    </div>
                  </button>
                </div>
                
                <div className="text-center text-xs text-gray-400 pt-2">
                  Your choice will be remembered for next time
                </div>
              </div>
            </div>
          )}
        </CardContent>
        
        {/* Signature Counter (bottom position for Sign & Send) */}
        {camp.campaign_type === "sign_and_send" && camp.sign_send_config?.show_counter && 
         camp.sign_send_config?.counter_position === "bottom" && 
         signatureCount >= (camp.sign_send_config?.show_counter_after || 0) && (
          <div className="text-center py-4 px-4 border-t" style={{ backgroundColor: t.secondary }}>
            <div className="text-3xl font-bold" style={{ color: t.primary }}>{signatureCount.toLocaleString()}</div>
            {(camp.sign_send_config?.counter_display_mode || "count_only") === "progress" ? (
              <>
                <div className="text-sm opacity-75" style={{ color: t.text }}>
                  {(camp.sign_send_config?.counter_progress_label || 'of {goal} signatures')
                    .replace('{count}', signatureCount.toLocaleString())
                    .replace('{goal}', (camp.sign_send_config?.signature_goal || 1000).toLocaleString())}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2 max-w-xs mx-auto">
                  <div 
                    className="h-2 rounded-full transition-all duration-500" 
                    style={{ 
                      backgroundColor: t.primary, 
                      width: `${Math.min(100, (signatureCount / (camp.sign_send_config?.signature_goal || 1000)) * 100)}%` 
                    }}
                  ></div>
                </div>
              </>
            ) : (
              <div className="text-sm opacity-75" style={{ color: t.text }}>{camp.sign_send_config?.counter_count_label || 'people have signed'}</div>
            )}
          </div>
        )}
      </Card>
      )}
    </div>
  );
}
